import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import {
  fontSize,
  fontWeight,
  spacing,
  mediaMax,
  borderRadius,
  isMediaMax,
} from 'presentations/views/styles';
import {
  black,
  gray04,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import { useCustomerStoryPage } from './hooks';
import Headline from 'presentations/views/components/atoms/Headline';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import CustomerListItem from 'presentations/views/components/molecules/CustomerStoryListItem';

import dynamic from 'next/dynamic';
import { CustomerStoryItem } from 'presentations/api/schema/contents';
import { ReactNode } from 'react';
import {
  contactPath,
  customerStoryListPath,
  dashboardPath,
  signUpPath,
  webPath,
} from 'presentations/routes/path';
import { useRouter } from 'next/router';
const Breadcrumbs = dynamic(() =>
  import('presentations/views/components/organisms/Breadcrumbs'),
);

interface Props {
  customerStory: CustomerStoryItem;
  siderbar?: ReactNode;
  header?: ReactNode;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const isMobile = isMediaMax('tablet');
  const router = useRouter();
  const { state, actions } = useCustomerStoryPage({
    customerStory: props.customerStory,
  });

  return (
    <ServicePageTemplate
      breadcrumbs={<Breadcrumbs routes={state.routes} />}
      header={props.header}
    >
      <Container>
        <Title variant="h1">{state.pageTitle}</Title>
        <SummaryContainer>
          <SummaryContent>
            <SummaryText>{props.customerStory.summary}</SummaryText>
          </SummaryContent>
          <SignUpContent>
            <SignUpContentTitle>{t('SignUpTitle')}</SignUpContentTitle>
            <SignUpContentButton
              size="large"
              href={signUpPath({
                to: webPath(dashboardPath()),
                locale: router.locale,
              })}
            >
              {t('SignUpButtonText')}
            </SignUpContentButton>
            <SignUpContentEp href={contactPath()}>
              <SignUpContentEpIcon name="chevron-right" />
              <SignUpContentEpText>{t('SignUpEpTitle')}</SignUpContentEpText>
            </SignUpContentEp>
          </SignUpContent>
        </SummaryContainer>
        <CustomerContainer color={props.customerStory.customerColor}>
          <CustomerContent>
            {props.customerStory.companyImageUrl && (
              <CustomerNameLabel>
                <CompanyImage
                  src={props.customerStory.companyImageUrl}
                  alt={props.customerStory.company}
                />
              </CustomerNameLabel>
            )}
            <CustomerName>
              {props.customerStory.customerDescription}
            </CustomerName>
          </CustomerContent>
          <CustomerImageWrapper>
            {/* <CustomerImageMask color={props.customerStory.customerColor} /> */}
            <CustomerImage
              image={
                isMobile
                  ? props.customerStory.thumbnailImageUrl!
                  : props.customerStory.coverImageUrl!
              }
            />
          </CustomerImageWrapper>
        </CustomerContainer>

        <CompanyContent>
          <CompanyContentInner>
            <CompanyContentMain>
              <CompanyName>{props.customerStory.company}</CompanyName>
              {props.customerStory.companyDescription && (
                <CompanyDescription>
                  {props.customerStory.companyDescription}
                </CompanyDescription>
              )}
            </CompanyContentMain>
            <CompanyMetaContent>
              <CompanyMetaContentTitle>{t('Industry')}</CompanyMetaContentTitle>
              <CompanyMetaContentText>
                {translate(`customerStory.${props.customerStory.industry}`)}
              </CompanyMetaContentText>
              <CompanyMetaContentTitle>{t('Role')}</CompanyMetaContentTitle>
              <CompanyMetaContentText>
                {translate(`customerStory.${props.customerStory.role}`)}
              </CompanyMetaContentText>
              <CompanyMetaContentTitle>{t('Stage')}</CompanyMetaContentTitle>
              <CompanyMetaContentText>
                {translate(`customerStory.${props.customerStory.stage}`)}
              </CompanyMetaContentText>
            </CompanyMetaContent>
          </CompanyContentInner>
        </CompanyContent>
        {state.maxCols > 0 && (
          <KpiList maxCols={state.maxCols}>
            {(props.customerStory.kpi1 ||
              props.customerStory.kpi1Description) && (
              <KpiListItem>
                <KpiListItemMark>
                  <KpiListItemIcon name="check" />
                </KpiListItemMark>
                <KpiListItemContent>
                  {props.customerStory.kpi1 && (
                    <KpiListItemText>
                      {props.customerStory.kpi1}
                    </KpiListItemText>
                  )}
                  {props.customerStory.kpi1Description && (
                    <KpiListItemDescription>
                      {props.customerStory.kpi1Description}
                    </KpiListItemDescription>
                  )}
                </KpiListItemContent>
              </KpiListItem>
            )}
            {(props.customerStory.kpi2 ||
              props.customerStory.kpi2Description) && (
              <KpiListItem>
                <KpiListItemMark>
                  <KpiListItemIcon name="check" />
                </KpiListItemMark>
                <KpiListItemContent>
                  {props.customerStory.kpi2 && (
                    <KpiListItemText>
                      {props.customerStory.kpi2}
                    </KpiListItemText>
                  )}
                  {props.customerStory.kpi2Description && (
                    <KpiListItemDescription>
                      {props.customerStory.kpi2Description}
                    </KpiListItemDescription>
                  )}
                </KpiListItemContent>
              </KpiListItem>
            )}
            {(props.customerStory.kpi3 ||
              props.customerStory.kpi3Description) && (
              <KpiListItem>
                <KpiListItemMark>
                  <KpiListItemIcon name="check" />
                </KpiListItemMark>
                <KpiListItemContent>
                  {props.customerStory.kpi3 && (
                    <KpiListItemText>
                      {props.customerStory.kpi3}
                    </KpiListItemText>
                  )}
                  {props.customerStory.kpi3Description && (
                    <KpiListItemDescription>
                      {props.customerStory.kpi3Description}
                    </KpiListItemDescription>
                  )}
                </KpiListItemContent>
              </KpiListItem>
            )}
          </KpiList>
        )}
        <ArticleProblemContent>
          {props.customerStory.problemHeadline && (
            <ArticleHeadline>
              <ArticleSubHeadline>{t('Problem')}</ArticleSubHeadline>
              {props.customerStory.problemHeadline}
            </ArticleHeadline>
          )}
          {props.customerStory.problem && (
            <ArticleProblemText>
              {props.customerStory.problem}
            </ArticleProblemText>
          )}

          {props.customerStory.solutionHeadline && (
            <ArticleHeadline>
              <ArticleSubHeadline>{t('Solution')}</ArticleSubHeadline>
              {props.customerStory.solutionHeadline}
            </ArticleHeadline>
          )}
          {props.customerStory.solution && (
            <ArticleProblemText>
              {props.customerStory.solution}
            </ArticleProblemText>
          )}

          {props.customerStory.benefitHeadline && (
            <ArticleHeadline>
              <ArticleSubHeadline>{t('Benefit')}</ArticleSubHeadline>
              {props.customerStory.benefitHeadline}
            </ArticleHeadline>
          )}
          {props.customerStory.benefit && (
            <ArticleProblemText>
              {props.customerStory.benefit}
            </ArticleProblemText>
          )}
        </ArticleProblemContent>
        <ArticleContent>
          <InterviewHeadline variant="h2">
            {t('Customer Interview')}
          </InterviewHeadline>
          <ArticleTextView
            dangerouslySetInnerHTML={{ __html: props.customerStory.interview }}
          />
        </ArticleContent>

        <CustomerListItemHeadline>{t('More Stories')}</CustomerListItemHeadline>

        <CustomerList>
          {state.customers.map((customer) => (
            <CustomerListItemWrap key={customer.id}>
              <CustomerListItem customer={customer} />
            </CustomerListItemWrap>
          ))}
        </CustomerList>
        <More>
          <Button color="primary" size="large" href={customerStoryListPath()}>
            {t('Learn more')}
          </Button>
        </More>
      </Container>
    </ServicePageTemplate>
  );
}

const Container = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: ${spacing(40)} ${spacing(16)};
  font-size: ${fontSize(16)};
  letter-spacing: 0.05em;
  line-height: 1.8;
  ${mediaMax.tablet} {
    margin: 0 ${spacing(12)};
  }
`;

const Title = styled(Headline)`
  font-size: ${fontSize(40)};
  margin-bottom: ${spacing(16)};
  line-height: initial;
  ${mediaMax.tablet} {
    font-size: ${fontSize(24)};
  }
`;
const SummaryContainer = styled.div`
  display: flex;
  margin-bottom: ${spacing(48)};
  ${mediaMax.tablet} {
    display: block;
  }
`;
const SummaryContent = styled.div`
  flex: 1;
`;
const SignUpContent = styled.div`
  width: 300px;
  margin-left: ${spacing(40)};
  background-color: ${gray04};
  border-radius: ${borderRadius(12)};
  height: fit-content;
  padding: ${spacing(16)};
  display: flex;
  flex-direction: column;
  align-items: center;
  ${mediaMax.tablet} {
    margin-top: ${spacing(40)};
    margin-left: 0;
    width: 100%;
  }
`;
const SignUpContentTitle = styled.div`
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(16)};
  text-align: center;
`;
const SignUpContentButton = styled(Button)`
  background-color: ${primary};
  color: ${whiteFix};
  margin-bottom: ${spacing(16)};
  border-radius: ${borderRadius(40)};
`;
const SignUpContentEp = styled.a`
  display: flex;
  align-items: center;
`;
const SignUpContentEpIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
`;
const SignUpContentEpText = styled.div`
  font-size: ${fontSize(14)};
`;
const SummaryText = styled.p`
  margin-bottom: ${spacing(20)};
`;
const CompanyContent = styled.div`
  background-color: ${white};
  border-radius: 0 0 ${borderRadius(12)} ${borderRadius(12)};
  padding: ${spacing(24)};
  margin-bottom: ${spacing(32)};
`;
const CompanyContentInner = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: ${spacing(16)};
  ${mediaMax.tablet} {
    display: block;
  }
`;
const CompanyDescription = styled.div`
  font-size: ${fontSize(14)};
`;
const CompanyContentMain = styled.div`
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
  }
`;
const CompanyImage = styled.img`
  width: auto;
  height: 24px;
`;
const CompanyName = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(16)};
`;
const CompanyMetaContent = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: ${spacing(16)};
  height: fit-content;
`;
const CompanyMetaContentTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(13)};
`;
const CompanyMetaContentText = styled.div`
  font-size: ${fontSize(13)};
`;
const KpiList = styled.div<{ maxCols: number }>`
  display: grid;
  border-top: 2px solid ${black};
  border-bottom: 1px solid ${black};
  padding: ${spacing(32)} 0;
  margin-bottom: ${spacing(32)};
  grid-template-columns: ${(props) =>
    `repeat(${props.maxCols}, minmax(1px, 1fr))`};
  ${mediaMax.tablet} {
    display: flex;
    flex-direction: column;
    padding: 0;
  }
`;
const KpiListItem = styled.div`
  border-right: 1px solid ${black};
  padding: ${spacing(16)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  :last-of-type {
    border-right: 0;
  }
  ${mediaMax.tablet} {
    flex-direction: row;
    justify-content: flex-start;
    border-right: 0;
    border-bottom: 1px solid ${black};
    :last-of-type {
      border-bottom: 0;
    }
  }
`;
const KpiListItemContent = styled.div`
  ${mediaMax.tablet} {
    margin-left: ${spacing(20)};
  }
`;
const KpiListItemMark = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${black};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const KpiListItemIcon = styled(Icon)`
  color: ${white};
  width: 24px !important;
  height: 24px !important;
`;
const KpiListItemText = styled.div`
  display: flex;
  justify-content: center;
  font-size: ${fontSize(40)};
  font-weight: ${fontWeight('bold')};
  ${mediaMax.tablet} {
    font-size: ${fontSize(32)};
    line-height: 1.4;
    justify-content: flex-start;
  }
`;
const KpiListItemDescription = styled.p`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
`;
const ArticleProblemContent = styled.div`
  padding: 0 ${spacing(40)};
  border-bottom: 2px solid ${black};
  margin-bottom: ${spacing(64)};
  ${mediaMax.tablet} {
    padding: 0;
  }
`;
const ArticleContent = styled.div``;
const InterviewHeadline = styled(Headline)`
  margin-top: 0;
  margin-bottom: ${spacing(56)};
  text-align: center;
  font-size: ${fontSize(32)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(24)};
    margin-bottom: ${spacing(40)};
  }
`;
const ArticleTextView = styled.div`

  /* border-bottom: 1px solid ${black}; */
  padding-bottom: ${spacing(24)};
  :last-of-type {
    /* border-bottom: 0; */
    padding-bottom: 0;
  }

  p {
    font-size: ${fontSize(18)};
  }
  h1,h2,h3,h4,h5,h6 {
    font-size: ${fontSize(20)} !important;
    font-weight: ${fontWeight('bold')} !important;
    margin-bottom: ${spacing(8)};
    margin-top: ${spacing(40)};

    /* border-top: 1px solid ${black}; */
    :first-of-type {
      /* border-top: 0; */
      margin-top: 0;
    }
  }
`;
const CustomerContainer = styled.div<{ color: string }>`
  background-color: ${(props) => props.color};
  display: grid;
  grid-template-columns: 45% 55%;
  border-radius: ${borderRadius(16)} ${borderRadius(16)} 0 0;
  height: 320px;
  overflow: hidden;
  ${mediaMax.tablet} {
    display: flex;
    flex-direction: column-reverse;
    height: initial;
  }
`;
const CustomerContent = styled.div`
  color: ${white};
  padding: ${spacing(48)} ${spacing(32)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${mediaMax.tablet} {
    padding: 0 ${spacing(20)} ${spacing(20)};
  }
`;
const CustomerNameLabel = styled.div`
  background-color: ${white};
  border-radius: ${borderRadius(8)};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  min-width: 240px;
  width: fit-content;
  margin-bottom: ${spacing(12)};
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const CustomerName = styled.div`
  font-size: ${fontSize(18)};
  font-weight: ${fontWeight('bold')};
  line-height: 1.6;
`;
const CustomerImageWrapper = styled.div`
  position: relative;
  ${mediaMax.tablet} {
    height: 300px;
    width: 100%;
  }
`;
// const CustomerImageMask = styled.div<{ color: string }>`
//   position: absolute;
//   top: 0;
//   left: 0;
//   right: 0;
//   bottom: 0;
//   background-color: ${(props) => props.color};
//   background: ${(props) =>
//     `linear-gradient(90deg, ${props.color} 0%, ${props.color}00 100%)`};
//   ${mediaMax.tablet} {
//     background: ${(props) =>
//       `linear-gradient(0deg, ${props.color} 0%, ${props.color}00 100%)`};
//   }
// `;
const CustomerImage = styled.div<{ image: string | null }>`
  background-image: url(${(props) => props.image});
  background-repeat: no-repeat;
  background-size: cover;
  width: 100%;
  height: 100%;
  background-position: center;
`;

const CustomerList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: ${spacing(16)};
  row-gap: ${spacing(16)};
  margin-top: ${spacing(48)};
  ${mediaMax.tablet} {
    display: block;
  }
`;
const ArticleHeadline = styled.h3`
  font-size: ${fontSize(18)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(16)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    flex-direction: column;
    font-size: ${fontSize(20)};
  }
`;
const ArticleSubHeadline = styled.div`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  margin-right: ${spacing(12)};
  background-color: ${black};
  color: ${white};
  padding: ${spacing(2)} ${spacing(8)};
  border-radius: ${borderRadius(8)};
  ${mediaMax.tablet} {
    padding: ${spacing(4)} ${spacing(24)};
    margin-right: 0;
    margin-bottom: ${spacing(8)};
  }
`;
const ArticleProblemText = styled.p`
  margin-bottom: ${spacing(32)};
  padding-bottom: ${spacing(32)};
  border-bottom: 1px solid ${black};
  :last-of-type {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

const CustomerListItemHeadline = styled.div`
  font-size: ${fontSize(32)};
  font-weight: ${fontWeight('bold')};
  margin-top: ${spacing(56)};
  display: flex;
  justify-content: center;
  ${mediaMax.tablet} {
    font-size: ${fontSize(24)};
  }
`;

const More = styled.div`
  margin-top: ${spacing(32)};
  text-align: center;
`;
const CustomerListItemWrap = styled.div`
  margin-bottom: ${spacing(24)};
  :last-child {
    margin-bottom: 0;
  }
`;
